import Joi from 'joi'
import { schemaOptions } from '../../../../shared/i18n'
import clsx from 'clsx'
import Avatar from 'react-avatar'
import { useState, useEffect, useRef } from 'react'
import { toast } from 'react-toastify'
import { useForm } from 'react-hook-form'
import { joiResolver } from '@hookform/resolvers/joi'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'

import Btn from '../../../../UI/Btn'
import TextField from '../../../../UI/TextField'
import SelectField from '../../../../UI/SelectField'
import BaseModal from '../../../../UI/BaseModal/BaseModal'
import TabBar from '../../../../UI/TabBar'
import AuthAvatar from '../../../../UI/AuthAvatar'
import RecordComments from '../../../../UI/RecordComments'
import ActionIcon from '../../../../UI/ActionIcon'
import StudentDocuments from './StudentDocuments'
import http from '../../../../shared/http'
import { types } from '../../../../shared/eventEmitter'
import useScrollTopOnTabChange from '../../../../shared/useScrollTopOnTabChange'
import { TApi, TLanguages, TRoles, RECORD_TYPES } from '../../../../shared/const'
import { isAlumni } from '../../../../shared/alumni'

import styles from './style.module.scss'
import { getFormattedDate, joiPhone, NAME_PATTERN } from '../../../../shared/helpers'
import DateTimeField from '../../../../UI/DateTimeField'

const schema = Joi.object({
	salutation: Joi.string().allow(''),
	firstName: Joi.string()
		.allow('')
		.min(2)
		.max(26)
		.pattern(NAME_PATTERN, 'name')
		.allow(''),
	lastName: Joi.string()
		.min(2)
		.max(26)
		.pattern(NAME_PATTERN, 'name')
		.allow(''),
	dateOfBirth: Joi.object().allow(null),
	phone: joiPhone.allow(''),
	street: Joi.string().min(2).max(50).allow(''),
	city: Joi.string().allow(''),
	houseNumber: Joi.number().allow(''),
	postalCode: Joi.number().allow('')
})

/*
 * Das Profilfenster einer Person, geoeffnet aus der Benutzer:innenliste, der
 * Mitarbeiter:innenliste eines Unternehmens oder der Schueler:innenliste einer
 * Lehrkraft.
 *
 * Bis 07.08.2026 stand alles auf einer Bahn untereinander: zehn Felder, darunter
 * die Handlungen zur Person, darunter „Speichern" — man scrollte an der
 * Wohnadresse vorbei, um den Ansichtsmodus zu erreichen. Jetzt liegen die
 * Felder in Reitern, so wie in „Mein Profil" und im Firmenprofil:
 *
 * - **Kopf** mit Foto, Name, E-Mail und Rolle. Die E-Mail ist ohnehin nicht
 *   aenderbar — als graues Feld nahm sie eine ganze Zeile ein.
 * - **Persoenliche Daten** · **Wohnadresse** · **Handlungen** · **Kommentare**.
 *
 * Die Reiter teilen sich *ein* Formular: Ausgehaengt verloere react-hook-form
 * die eingetippten Werte, und „Speichern" schickt weiterhin alle Felder auf
 * einmal — egal, in welchem Reiter sie stehen.
 */
const TAB_KEYS = {
	PERSONAL: 'personal',
	ADDRESS: 'address',
	// Nur für Lehrkräfte und nur bei Schüler:innen: der Stand der persönlichen
	// Nachweise, mit Vorschau und — wo freigegeben — Uploadfeld (Anforderung #94).
	DOCUMENTS: 'documents',
	ACTIONS: 'actions',
	COMMENTS: 'comments'
}

// Welches Feld in welchem Reiter steht. Gebraucht fuer die Ampel an der
// Reiterleiste und um beim Speichern zum beanstandeten Feld zu springen.
const TAB_FIELDS = {
	[TAB_KEYS.PERSONAL]: ['salutation', 'firstName', 'lastName', 'dateOfBirth', 'phone'],
	[TAB_KEYS.ADDRESS]: ['street', 'city', 'houseNumber', 'postalCode']
}

function StudentProfile(props) {
	const { t, i18n } = useTranslation()
	const {
		reset,
		register,
		control,
		watch,
		setValue,
		handleSubmit,
		trigger,
		formState: { errors, dirtyFields }
	} = useForm({
		resolver: joiResolver(schema, {
			errors: { labels: false, language: localStorage.getItem('i18nextLng') },
			...schemaOptions
		})
	})

	const [isLoading, setLoading] = useState(false)
	const [id, setId] = useState(null)
	const [role, setRole] = useState('')
	// Was der Kopf zeigt und die Felder nicht mehr enthalten: Foto, E-Mail und
	// die Zuordnung (Unternehmen bei Schueler:innen, Schule bei Lehrkraeften).
	const [person, setPerson] = useState(null)
	const [funcUser, setFuncUser] = useState({ fetchUsers: () => {} })
	// Handlungen zu dieser Person, von der aufrufenden Ansicht mitgegeben —
	// dieselben, die dort im Drei-Punkte-Menue stehen. Wer ein Profil geoeffnet
	// hat, soll dafuer nicht in die Liste zurueckgehen muessen.
	const [actions, setActions] = useState([])
	// Reiter „Kommentare": nur fuer die Verwaltung. Die Schnittstelle dahinter
	// steht ausschliesslich Admins offen — eine Lehrkraft saehe ein Feld, das
	// beim Speichern 403 antwortet.
	const [tab, setTab] = useState(TAB_KEYS.PERSONAL)

	const tabsRef = useRef(null)
	useScrollTopOnTabChange(tab, tabsRef)

	// Der Name im Kopf folgt der Eingabe: Wer ihn im Reiter darunter berichtigt,
	// sieht sonst bis zum Speichern weiter den alten oben stehen.
	const name = [watch('firstName'), watch('lastName')].filter(Boolean).join(' ')

	useEffect(() => {
		if (Object.keys(errors).length) trigger()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [localStorage.getItem('i18nextLng')])

	const onSubmit = (data, closeModal) => {
		setLoading(true)

		const schema = {}

		if (data.salutation) schema.salutation = data.salutation
		if (data.firstName) schema.firstName = data.firstName
		if (data.lastName) schema.lastName = data.lastName
		if (data.dateOfBirth) schema.dateOfBirth = data.dateOfBirth
		if (data.phone) schema.phone = data.phone
		if (data.street) schema.street = data.street
		if (data.city) schema.city = data.city
		if (data.houseNumber) schema.houseNumber = data.houseNumber
		if (data.postalCode) schema.postalCode = data.postalCode

		http.put(TApi.ADMIN_PUT_USER(id), schema)
			.then(() => {
				toast.success(t('User profile has been updated.'))
				setId(null)
				funcUser.fetchUsers()
				closeModal()
			})
			.finally(() => setLoading(false))
	}

	/*
	 * Ein beanstandetes Feld kann in einem zugeklappten Reiter stehen — dann
	 * passierte auf „Speichern" scheinbar nichts. Deshalb wird der erste Reiter
	 * mit Beanstandung geoeffnet.
	 */
	const onInvalid = invalid => {
		const target = [TAB_KEYS.PERSONAL, TAB_KEYS.ADDRESS].find(key =>
			TAB_FIELDS[key].some(field => invalid[field])
		)
		if (target) setTab(target)
	}

	const handleOpen = (profile, fetchUsers, profileActions = []) => {
		setId(profile.id)
		setTab(TAB_KEYS.PERSONAL)
		setRole(profile.role)
		setPerson(profile)
		setActions(profileActions)
		profile.salutation && setValue('salutation', profile.salutation)
		profile.firstName && setValue('firstName', profile.firstName)
		profile.lastName && setValue('lastName', profile.lastName)
		profile.dateOfBirth && setValue('dateOfBirth', new Date(profile.dateOfBirth))
		profile.phone && setValue('phone', profile.phone)
		profile.street && setValue('street', profile.street)
		profile.city && setValue('city', profile.city)
		profile.houseNumber && setValue('houseNumber', profile.houseNumber)
		profile.postalCode && setValue('postalCode', profile.postalCode)
		setFuncUser({ fetchUsers })
	}

	/*
	 * Ampel an der Reiterleiste: Ein Punkt erscheint nur, wo etwas beanstandet
	 * ist (`done: false`). Ein gruener Punkt an jedem fehlerfreien Reiter waere
	 * nur Farbe ohne Aussage — hier ist nichts abzuhaken, es sind Stammdaten.
	 */
	const tabError = key => (TAB_FIELDS[key].some(field => errors[field]) ? false : undefined)

	/*
	 * Der Reiter „Nachweise" steht nur Lehrkräften offen und nur bei
	 * Schüler:innen: Die Schnittstelle dahinter läuft über die Betreuung, und
	 * eine Lehrkraft hat keine Einverständniserklärung.
	 *
	 * Der Punkt an der Reiterleiste zeigt an, dass etwas offen ist — dieselbe
	 * Ampel wie bei den beanstandeten Feldern. Gemessen wird an dem Stand, den
	 * die Liste ohnehin mitgibt; das Fenster muss dafür nichts nachladen.
	 */
	const showDocuments = props.isTeacher && role === TRoles.STUDENT

	/*
	 * Alumni (#213) lesen nur: Felder gesperrt, kein Speichern, keine
	 * Handlungen. Wohnadresse, Telefon und Geburtsdatum schickt der Server
	 * ihnen gar nicht — die Felder stünden leer da, als fehlten die Angaben.
	 */
	const locked = props.isTeacher || props.isReadOnly
	const sichtbareHandlungen = props.isReadOnly ? [] : actions

	const documentsDone =
		person?.Documents?.length && !person?.rejectedParentalConfirmation ? undefined : false

	const tabs = [
		{ key: TAB_KEYS.PERSONAL, label: 'personalData', done: tabError(TAB_KEYS.PERSONAL) },
		...(props.isReadOnly
			? []
			: [{ key: TAB_KEYS.ADDRESS, label: 'Residential address', done: tabError(TAB_KEYS.ADDRESS) }]),
		...(showDocuments ? [{ key: TAB_KEYS.DOCUMENTS, label: 'Proofs', done: documentsDone }] : []),
		...(sichtbareHandlungen.length
			? [{ key: TAB_KEYS.ACTIONS, label: 'Actions', count: sichtbareHandlungen.length }]
			: []),
		...(props.canComment ? [{ key: TAB_KEYS.COMMENTS, label: 'Comments' }] : [])
	]

	// Zuordnung im Kopf: das Unternehmen einer Schueler:in, die Schule einer
	// Lehrkraft — je nachdem, was die aufrufende Ansicht mitgegeben hat.
	const affiliation = person?.EmploymentCompany?.name || person?.School?.name
	const companyType = person?.EmploymentCompany?.companyType
	// „CEO / Geschäftsführung" steht am eigenen Feld, alles andere am
	// Arbeitsbereich — dieselbe Regel wie in der Spalte „Funktion" der Liste.
	const workArea = person?.isCEO ? 'CEO / Geschäftsführung' : person?.primaryWorkArea

	return (
		<BaseModal
			onClose={reset}
			onOpen={handleOpen}
			nameEvent={types.openStudentProfile}
			isDirty={Boolean(Object.keys(dirtyFields).length)}
			title={t('profile')}
			isLoading={isLoading}
			/*
			 * Zwei Felder nebeneinander (je 337 px) plus Innenabstaende. Breiter
			 * musste das Fenster nur sein, solange die Handlungen als Knopfreihe
			 * unter den Feldern standen — die haben jetzt ihren eigenen Reiter.
			 */
			maxWidth={760}
		>
			{({ closeModal }) => (
				<>
					<div className={styles.header}>
						<AuthAvatar
							hashName={person?.avatarHashName}
							alt=""
							className={styles.avatar}
							fallback={
								<Avatar name={name || '?'} size="52" round color="#c3edef" fgColor="#22404d" />
							}
						/>
						<div className={styles.identity}>
							<h2 className={styles.name}>{name}</h2>
							{person?.email && <span className={styles.mail}>{person.email}</span>}
							<div className={styles.chips}>
								{role && (
									<span className={styles.chip}>
										{t(role === TRoles.STUDENT ? 'Student' : 'Teacher')}
									</span>
								)}
								{affiliation && (
									<span className={clsx(styles.chip, styles.chipAffiliation)}>
										{affiliation}
									</span>
								)}
								{/* Was die Liste dahinter in eigenen Spalten zeigt —
								    Unternehmensform, Funktion, seit wann angemeldet.
								    Im Fenster stand davon bisher nichts, obwohl es
								    genau die Angaben sind, wegen derer man es
								    öffnet. */}
								{companyType && (
									<span className={clsx(styles.chip, styles.chipAffiliation)}>
										{companyType}
									</span>
								)}
								{workArea && (
									<span className={clsx(styles.chip, styles.chipAffiliation)}>
										{t(workArea)}
									</span>
								)}
								{person?.createdAt && (
									<span className={clsx(styles.chip, styles.chipMuted)}>
										{t('Registration Date')}: {getFormattedDate(person.createdAt)}
									</span>
								)}
							</div>
						</div>
					</div>

					<TabBar
						className={styles.tabs}
						tabs={tabs}
						active={tab}
						onChange={setTab}
						tabsRef={tabsRef}
					/>

					{tab === TAB_KEYS.COMMENTS && (
						<RecordComments entityType={RECORD_TYPES.USER} entityId={id} />
					)}

					{tab === TAB_KEYS.DOCUMENTS && (
						<StudentDocuments student={person} onChange={() => funcUser.fetchUsers()} />
					)}

					{/* Das Formular bleibt eingehaengt, auch wenn die Kommentare
					    offen sind: Ausgehaengt gingen bereits eingetippte
					    Aenderungen verloren. Aus demselben Grund werden die
					    Reiter darin ausgeblendet statt ausgehaengt. */}
					<form
						className={clsx(
							styles.root,
							(tab === TAB_KEYS.COMMENTS || tab === TAB_KEYS.DOCUMENTS) && styles.hidden
						)}
						onSubmit={handleSubmit(
							form =>
								onSubmit(form, () => {
									setId(null)
									closeModal()
								}),
							onInvalid
						)}
					>
						<div className={clsx(tab !== TAB_KEYS.PERSONAL && styles.hidden)}>
							<SelectField
								disabled={isLoading || locked}
								mb={20}
								label={t('salutation')}
								control={control}
								name="salutation"
								error={errors.salutation}
								placeholder="Salutation"
								options={props.salutations?.map(v => ({
									value: v.value_EN,
									label: i18n.language === TLanguages.EN ? v.value_EN : v.value_DE
								}))}
							/>
							<section className={styles.flexContainer}>
								<TextField
									disabled={isLoading || locked}
									mb={20}
									label={t('First Name')}
									placeholder={t('First Name')}
									error={errors.firstName}
									register={register('firstName')}
								/>
								<TextField
									disabled={isLoading || locked}
									mb={20}
									label={t('Last Name')}
									placeholder={t('Last Name')}
									error={errors.lastName}
									register={register('lastName')}
								/>
							</section>
							<section className={styles.flexContainer}>
								{role === TRoles.STUDENT && !props.isReadOnly && (
									<DateTimeField
										disabled={isLoading || locked}
										mb={20}
										label={t('Date of Birth')}
										placeholder="Date of Birth"
										name="dateOfBirth"
										error={errors.dateOfBirth}
										control={control}
										dateFormat="dd.MM.yyyy"
									/>
								)}
								{!props.isReadOnly && (
									<TextField
										disabled={isLoading || locked}
										mb={20}
										label={t('phone')}
										placeholder={t('phone')}
										error={errors.phone}
										register={register('phone')}
									/>
								)}
							</section>
						</div>

						<div className={clsx(tab !== TAB_KEYS.ADDRESS && styles.hidden)}>
							<section className={styles.flexContainer}>
								<TextField
									disabled={isLoading || locked}
									mb={20}
									label={t('Street')}
									placeholder={t('Street')}
									error={errors.street}
									register={register('street')}
								/>
								<TextField
									disabled={isLoading || locked}
									mb={20}
									label={t('House number')}
									placeholder={t('House number')}
									error={errors.houseNumber}
									register={register('houseNumber')}
								/>
							</section>
							<section className={styles.flexContainer}>
								<TextField
									disabled={isLoading || locked}
									mb={20}
									label={t('Zip Code')}
									placeholder={t('Zip Code')}
									error={errors.postalCode}
									register={register('postalCode')}
								/>
								<TextField
									disabled={isLoading || locked}
									mb={20}
									label={t('City')}
									placeholder={t('City')}
									error={errors.city}
									register={register('city')}
								/>
							</section>
						</div>

						{/* Die Handlungen aendern nichts an den Feldern — deshalb
						    steht in ihrem Reiter auch kein Speichern-Knopf.

						    Seit 02.11.2026 eine Liste statt einer Knopfreihe: Die
						    Umrandungsknöpfe waren so breit wie ihr Text und
						    standen wie ein Stapel verschieden langer Balken da;
						    ob eine Handlung gesperrt ist und warum, sah man nur am
						    blasseren Grau. Jetzt hat jede Zeile Symbol, Titel und
						    einen Satz dazu — auch, warum sie gerade nicht geht.
						    Endgültiges („Konto löschen", `danger`) steht abgesetzt
						    darunter, damit es nicht im Vorbeigehen erwischt wird. */}
						<div className={clsx(styles.actions, tab !== TAB_KEYS.ACTIONS && styles.hidden)}>
							{[
								sichtbareHandlungen.filter(action => !action.danger),
								sichtbareHandlungen.filter(action => action.danger)
							]
								.filter(group => group.length)
								.map((group, groupIndex) => (
									<ul
										key={groupIndex}
										className={clsx(styles.actionList, group[0].danger && styles.actionListDanger)}
									>
										{group.map((action, index) => (
											<li key={index}>
												<button
													type="button"
													className={clsx(
														styles.actionRow,
														action.danger && styles.actionRowDanger
													)}
													disabled={isLoading || action.disabled}
													onClick={() => action.onClick(closeModal)}
												>
													<span className={styles.actionIcon}>
														{action.icon && <ActionIcon name={action.icon} size={20} />}
													</span>
													<span className={styles.actionText}>
														<span className={styles.actionLabel}>
															{action.profileLabel || action.label}
														</span>
														{action.hint && (
															<span className={styles.actionHint}>{action.hint}</span>
														)}
													</span>
													{!action.disabled && (
														<span className={styles.actionChevron} aria-hidden="true">
															›
														</span>
													)}
												</button>
											</li>
										))}
									</ul>
								))}
						</div>

						{!locked && tab !== TAB_KEYS.ACTIONS && tab !== TAB_KEYS.DOCUMENTS && (
							<Btn
								loading={isLoading}
								width={155}
								className={styles.btnSubmit}
								type="submit"
								icon="send"
								iconSize={{ w: 15, h: 15 }}
								iconColor="#fff"
							>
								{t('save')}
							</Btn>
						)}
					</form>
				</>
			)}
		</BaseModal>
	)
}

StudentProfile.propTypes = {
	salutations: PropTypes.array.isRequired,
	isTeacher: PropTypes.bool,
	isReadOnly: PropTypes.bool,
	canComment: PropTypes.bool
}
const mapStateToProps = state => ({
	salutations: state.options.salutations,
	isTeacher: state.user.profile.role === TRoles.TEACHER,
	isReadOnly: isAlumni(state.user.profile.role),
	canComment: [TRoles.ADMIN, TRoles.FEDERAL_ADMIN].includes(state.user.profile.role)
})
export default connect(mapStateToProps)(StudentProfile)
